import * as React from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { Link, useHistory, useParams } from 'react-router-dom';
import styled from 'styled-components';
import { Panel } from '@rtkwlf/ui';
import { Breadcrumb, BreadcrumbItem } from 'reactstrap';
import { APIError } from '../../../../../APIError';
import { PageTitle } from '../../../../../widgets/PageTitle';
import Viewport from '../../../../../widgets/Viewport';
import ViewHeader from '../../../../../widgets/ViewHeader';
import { OnboardingSensorForm } from './Form';
import { onboardingSelector } from '../../../../../reducers/onboarding';
import ErrorJumbotron from '../../../../../widgets/ErrorJumbotron';
import LoadingIndicator from '../../../../../widgets/LoadingIndicator';
import { HTTP_STATUS, ROUTE_PATH } from '../../../../../constants';
import {
  BAD_MODEL_RECOMMENDATIONS,
  fetchRecommendedModel,
  getFormValues,
  getSensorModelFromFormValues,
  SensorFormValues,
} from './constants';
import { ONBOARDING_ACTIONS } from '../../../../../actions/onboarding';
import { apiRequest } from '../../../../../APIClient';
import { Formik } from 'formik';
import { createSensorScannerValidationSchema } from '../../../validationSchemas';
import { sessionSelector } from '../../../../../reducers/session';
import { ScrollSectionKeys } from '../../OnboardingSiteConfig';

const NOT_FOUND = new APIError('Sensor not found', HTTP_STATUS.NOT_FOUND);

const ErrorPanel = styled(Panel)`
  margin-left: 0;
  margin-right: 0;
  min-height: 225px;
`;

/**
 * This view should guard against scenarios where the sensor is not editable
 *  e.g. still loading, not found
 *  and render an error banner instead of the form
 */
export const OnboardingSensorEditView: React.FunctionComponent = () => {
  const isLoading = useSelector(onboardingSelector.getSensorsSectionLoading);
  const genericSeriesEnabled = useSelector(
    onboardingSelector.showGenericSeries,
  );

  const sensorCapabilities = useSelector(
    onboardingSelector.getSensorCapabilities,
  );

  const { uuid } = useParams<{ uuid: string }>();
  const sensor = useSelector(onboardingSelector.getSensorByUuid(uuid));
  const isAwnUser = useSelector(sessionSelector.isAwnUser);
  const [recommendedModel, setRecommendedModel] = React.useState<string>('');
  const dispatch = useDispatch();
  const history = useHistory();

  const [currentSeries, setCurrentSeries] = React.useState('');

  const onSubmitSensor = React.useCallback(
    async (values: SensorFormValues) => {
      const sensor = getSensorModelFromFormValues(values);
      if (genericSeriesEnabled && !sensor.modelChanged) {
        const recommendedModel = await fetchRecommendedModel(sensor);
        if (!BAD_MODEL_RECOMMENDATIONS.includes(recommendedModel)) {
          sensor.model = recommendedModel;
        } else {
          sensor.model = '';
        }
      }
      dispatch(ONBOARDING_ACTIONS.EDIT_SENSOR(uuid, sensor));
      dispatch(apiRequest.saveOBSensors());
      history.push({
        pathname: ROUTE_PATH.ONBOARDING.MAIN,
        search: 'view=sensors',
      });
    },
    [dispatch, uuid, genericSeriesEnabled, history],
  );

  React.useEffect(() => {
    const emptyCapabilities =
      !sensor?.currentCapabilities ||
      Object.keys(sensor.currentCapabilities).length === 0;
    const getRecommendedModel = async () => {
      if (isAwnUser && genericSeriesEnabled && !emptyCapabilities) {
        const recommendedModel = await fetchRecommendedModel(sensor);
        setRecommendedModel(recommendedModel);
      }
    };

    getRecommendedModel();
  }, [dispatch, isAwnUser, genericSeriesEnabled, sensor]);

  let errorContent: React.ReactNode;

  if (isLoading) {
    errorContent = (
      <LoadingIndicator size="lg" centered>
        Loading sensor...
      </LoadingIndicator>
    );
  } else if (!sensor) {
    errorContent = <ErrorJumbotron error={NOT_FOUND} />;
  }

  const sensorNames = useSelector(onboardingSelector.getSensorNames);
  const validationSchema = React.useMemo(
    () =>
      createSensorScannerValidationSchema({
        genericSeriesEnabled,
        schemas: sensorCapabilities[currentSeries],
        existingNames: sensorNames,
        initialName: sensor?.name,
      }),
    [
      currentSeries,
      genericSeriesEnabled,
      sensor,
      sensorCapabilities,
      sensorNames,
    ],
  );

  if (errorContent) {
    return (
      <Viewport>
        <Breadcrumb>
          <BreadcrumbItem>
            <Link to={ROUTE_PATH.ONBOARDING.MAIN}>Onboarding Overview</Link>
          </BreadcrumbItem>
          <BreadcrumbItem active>Edit Sensor</BreadcrumbItem>
        </Breadcrumb>
        <PageTitle title="Edit Sensor" />
        <ViewHeader title="Edit Sensor" />

        <ErrorPanel className="min-height-for-loading-indicator">
          {errorContent}
        </ErrorPanel>
      </Viewport>
    );
  }

  const defaultSensorValues: SensorFormValues = getFormValues(sensor!);

  const content = errorContent ? (
    <ErrorPanel className="min-height-for-loading-indicator">
      {errorContent}
    </ErrorPanel>
  ) : (
    <Formik
      innerRef={(e) => {
        setCurrentSeries(e?.values?.series ?? '');
      }}
      initialValues={defaultSensorValues}
      onSubmit={onSubmitSensor}
      validationSchema={validationSchema}
      enableReinitialize={true}
    >
      {genericSeriesEnabled ? (
        <OnboardingSensorForm
          recommendedModel={recommendedModel}
          view={ScrollSectionKeys.SENSORS}
        />
      ) : (
        <OnboardingSensorForm view={ScrollSectionKeys.SENSORS} />
      )}
    </Formik>
  );

  const title = sensor ? 'Edit Sensor - ' + sensor.name : 'Edit Sensor';

  return (
    <Viewport>
      <Breadcrumb>
        <BreadcrumbItem>
          <Link to={ROUTE_PATH.ONBOARDING.MAIN}>Onboarding Overview</Link>
        </BreadcrumbItem>
        <BreadcrumbItem active>Edit Sensor</BreadcrumbItem>
      </Breadcrumb>
      <PageTitle title={title} />
      <ViewHeader title={title} />
      {content}
    </Viewport>
  );
};
